import { Component } from 'react'
import Autosuggest from 'react-autosuggest'
import s from './Search.scss'
import { makeGetRequest } from 'utils/api'
import { groupBy } from 'utils/helpers'
import { typeMapper, urlMapper } from './helpers'
import { Link } from 'react-router-dom'
import parse from 'autosuggest-highlight/parse'
import match from 'autosuggest-highlight/match'
import throttle from 'lodash/debounce'

// Teach Autosuggest how to calculate suggestions for any given input value.
const getSuggestions = async (value, cb, count) => {
  const inputValue = value.trim().toLowerCase()
  const inputLength = inputValue.length
  const query = value.trim()

  if (query === '') {
    return []
  }

  const response = await makeGetRequest(
    `/v1/search/?query=${encodeURIComponent(query)}&count=${count}`
  )
  const results = groupBy(response, 'type')
  const values = Object.keys(results)
    .map(section => {
      return {
        title: section,
        results: results[section].map(result => ({
          id: result.id,
          name: result.title,
          type: result.type
        }))
      }
    })
    .filter(section => section.results.length > 0)

  const arr = [
    {
      title: 'View All Search Results',
      results: [],
      global: true,
      query
    },
    ...values
  ]
  cb && cb(arr)

  return inputLength === 0 ? [] : values
}

// When suggestion is clicked, Autosuggest needs to populate the input
// based on the clicked suggestion. Teach Autosuggest how to calculate the
// input value for every given suggestion.
const getSuggestionValue = suggestion => suggestion.name

// Use your imagination to render suggestions.
const renderSuggestion = (suggestion, { query }) => {
  const matches = match(suggestion.name, query)
  const parts = parse(suggestion.name, matches)

  return (
    <span className={s.resultsItem}>
      {parts.map((part, index) => {
        const className = part.highlight ? s.highlight : null

        return (
          <span className={className} key={index}>
            {part.text}
          </span>
        )
      })}
    </span>
  )
}

const renderSectionTitle = section => {
  if (section.global) {
    return (
      <span>
        <Link to={`/search?query=${encodeURIComponent(section.query)}`}>{section.title}</Link>
      </span>
    )
  }

  return (
    <span>
      Top results in{' '}
      {section.title === 'PROPOSAL' ? (
        <span>{typeMapper[section.title]}</span>
      ) : (
        <Link to={urlMapper[section.title]}>{typeMapper[section.title]}</Link>
      )}
    </span>
  )
}

const getSectionSuggestions = section => section.results

const shouldRenderSuggestions = value => value.trim().length > 1

class Search extends Component {
  state = {
    value: '',
    suggestions: []
  }

  onChange = (event, { newValue }) => {
    this.setState({
      value: newValue
    })
  }

  // Autosuggest will call this function every time you need to update suggestions.
  _onSuggestionsFetchRequested = async ({ value }) => {
    this.props.setIsSearching(true)
    getSuggestions(
      value,
      suggestions => {
        this.setState({
          suggestions
        })
        this.props.setIsSearching(false)
      },
      this.props.count
    )
  }

  onSuggestionsFetchRequested = throttle(this._onSuggestionsFetchRequested.bind(this), 500)

  // Autosuggest will call this function every time you need to clear suggestions.
  onSuggestionsClearRequested = () => {
    this.setState({
      suggestions: []
    })
  }

  onSuggestionSelected = (event, { suggestion }) => {
    // TODO: use router location after v6 gets it fixed
    window.location.href = `${urlMapper[suggestion.type]}${suggestion.id}`

    this.setState({
      value: ''
    })
  }

  render() {
    const { value, suggestions } = this.state

    // Autosuggest will pass through all these props to the input.
    const inputProps = {
      placeholder: 'Search...',
      value,
      onChange: this.onChange
    }

    return (
      <Autosuggest
        multiSection
        id={this.props.id}
        suggestions={suggestions}
        onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
        onSuggestionsClearRequested={this.onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        renderSectionTitle={renderSectionTitle}
        getSectionSuggestions={getSectionSuggestions}
        inputProps={inputProps}
        shouldRenderSuggestions={shouldRenderSuggestions}
        onSuggestionSelected={this.onSuggestionSelected}
      />
    )
  }
}

export default Search
